/**
 * The Part 11 signature and Part 11 PIN as spdata-api has them (ENG-9103, ENG-9104, ADR 0013). The
 * server holds and checks the PIN; the client sends it with a save and never stores or compares it.
 * Every code here is spdata-api's wire contract: `src/entry/part11-signature.ts`,
 * `src/entry/part11-signature.service.ts` and `src/part11-pin/part11-pin.controller.ts`.
 */

/** What a Part 11 signature says the signer did. The client translates the code wherever it draws it. */
export const SIGNATURE_MEANINGS = ['approved', 'reviewed', 'owner'] as const;
export type SignatureMeaning = (typeof SIGNATURE_MEANINGS)[number];

/** `review` is "Add signature", a signature that changes no answer. */
export type SignatureType = 'update' | 'review';

/** What a save on a Part 11 study sends as `signature`. */
export interface Part11Signature {
  meaning: SignatureMeaning;
  comment: string;
  type: SignatureType;
  pin: string;
}

/**
 * How the server recorded a save's signature, sent back on the saved entry as `signatureCheck`. A
 * save that lands is never refused on it until ENG-9110, so `method: 'none'` with `wrong`, `locked`
 * or `not-set` is a save that went through unsigned.
 */
export type Part11SignatureCheck =
  | { method: 'pin' }
  | { method: 'none'; reason: 'unsigned' | 'public' | 'unavailable' | 'not-set' | 'locked' }
  | { method: 'none'; reason: 'wrong'; attemptsLeft: number };

/** A signature check that did not confirm the signature the save carried. */
export type SignatureRefusal = Extract<Part11SignatureCheck, { method: 'none' }>;

/** The signature on an activity row, as `entry/:id/activities` sends it. */
export interface SignatureView {
  meaning: SignatureMeaning | null;
  comment: string | null;
  type: SignatureType | null;
  method: 'pin' | 'none';
  signedAt: string;
  viaPublicToken?: true;
}

/**
 * The sentences the web sent as `reason.reason` before the meaning became a code, and the meaning
 * each one is. Rows written then keep the sentence, and the history dialog reads it back through
 * this (ENG-9100 decision 11, spdata-api's `LEGACY_REASON_MEANINGS`).
 */
export const LEGACY_REASON_MEANINGS: Readonly<Record<string, SignatureMeaning>> = {
  'I approve this document': 'approved',
  'I have reviewed this document': 'reviewed',
  'I am the owner of this document': 'owner'
};

/** The caller's own Part 11 PIN, as `GET part11-pin` answers. */
export interface Part11PinStatus {
  set: boolean;
  locked: boolean;
  /** Digits in a new PIN. */
  length: number;
}

/** The `reason` codes the PIN routes refuse with, `REFUSALS` in spdata-api's `part11-pin.controller.ts`. */
export const PIN_REFUSAL_REASONS = [
  'invalid-pin', 'no-code', 'code-expired', 'wrong-code', 'not-set', 'wrong', 'too-soon', 'send-failed', 'locked'
] as const;

/**
 * Why a PIN route refused, as its `reason` code, or `failed` for a request that carried none: the
 * network, a 500, a signed-out caller.
 */
export type Part11PinRefusalReason = (typeof PIN_REFUSAL_REASONS)[number] | 'failed';

export interface Part11PinRefusal {
  reason: Part11PinRefusalReason;
  attemptsLeft?: number;
  retryAfterSeconds?: number;
  /** Digits a new PIN must have, on `invalid-pin`. */
  length?: number;
}

export type Part11PinAnswer<T> = { ok: true; value: T } | ({ ok: false } & Part11PinRefusal);
